import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, ExternalLink, Loader2, Power, Users, Info, Sparkles, Layers, Building2, SlidersHorizontal, FileText } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { errMsg } from '@/components/jobfairs/fairApi';
import { StatusBadge } from './JobFairList';
import { BasicTab, HeroTab, PartnersTab, FooterTab } from './EditorTabs';
import { SectionsTab } from './SectionsTab';
import { FormSettingsTab } from './FormSettingsTab';

const TABS = [
  ['basic', 'Basic Details', Info, BasicTab], ['hero', 'Hero & Highlights', Sparkles, HeroTab],
  ['sections', 'Sections', Layers, SectionsTab], ['partners', 'Organizers & Partners', Building2, PartnersTab],
  ['form', 'Form Settings', SlidersHorizontal, FormSettingsTab], ['footer', 'Footer & Theme', FileText, FooterTab],
];
const SKIP = ['id', 'status', 'effective_status', 'candidate_count', 'employer_count', 'created_at', 'created_by', 'updated_at'];

export default function JobFairEditor() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/job-fairs/admin/${id}`).then((r) => setF(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);

  if (!f) return <div className="p-8 text-slate-500">Loading…</div>;
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([k]) => !SKIP.includes(k)));
      const r = await api.put(`/job-fairs/admin/${id}`, body);
      setF(r.data); setDirty(false); toast.success('Job fair saved');
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    if (dirty) await save();
    try { const r = await api.put(`/job-fairs/admin/${id}/status`, { status }); setF((p) => ({ ...p, status: r.data.status, effective_status: r.data.effective_status })); toast.success(`Status: ${status}`); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="jobfair-editor-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" asChild><Link to="/admin/jobfairs" data-testid="editor-back-button"><ArrowLeft className="w-5 h-5" /></Link></Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2"><h1 className="text-xl font-bold text-slate-900 truncate">{f.name}</h1><StatusBadge fair={f} /></div>
            <p className="text-xs text-slate-500">/jobfairs/{f.slug}{dirty && <span className="ml-2 text-amber-600 font-medium">• Unsaved changes</span>}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild><a href={`/jobfairs/${f.slug}?preview=1`} target="_blank" rel="noreferrer" data-testid="editor-preview-button"><ExternalLink className="w-4 h-4 mr-1" />Preview</a></Button>
          <Button variant="outline" asChild><Link to={`/admin/jobfairs/${id}/registrations`} data-testid="editor-registrations-button"><Users className="w-4 h-4 mr-1" />Registrations</Link></Button>
          {f.status === 'active'
            ? <Button variant="outline" className="text-amber-700 border-amber-300" onClick={() => setStatus('deactivated')} data-testid="editor-deactivate-button"><Power className="w-4 h-4 mr-1" />Deactivate</Button>
            : <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus('active')} data-testid="editor-activate-button"><Power className="w-4 h-4 mr-1" />Activate</Button>}
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="editor-save-button">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
          </Button>
        </div>
      </div>
      <Tabs defaultValue="basic">
        <TabsList className="flex flex-wrap h-auto bg-white border p-1 justify-start">
          {TABS.map(([v, l, Icon]) => <TabsTrigger key={v} value={v} data-testid={`editor-tab-${v}`} className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"><Icon className="w-4 h-4 mr-1.5" />{l}</TabsTrigger>)}
        </TabsList>
        {TABS.map(([v, , , Comp]) => <TabsContent key={v} value={v} className="mt-4"><Comp f={f} set={set} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
